/* eslint-disable react/no-array-index-key */
import { Text, useTexture } from '@react-three/drei';
import { useAppSelector } from 'app/hooks';
import {
  EdgeItemsType,
  EdgeItemType,
  ExposureDirectionType,
  PositionAndDimensionsType,
  PositionType
} from 'data/types';
import { useMemo } from 'react';
import {
  BufferAttribute,
  Color,
  DoubleSide,
  RepeatWrapping,
  ShapeGeometry,
  Texture
} from 'three';
import {
  computeEdgeItemDisplay,
  edgeItemMaterialMap
} from 'utils/3d-edge-items';
import {
  getOpacityMaterialProps,
  getOpacityRenderOrder,
  OVERLAY_RENDER_ORDER
} from 'utils/3d-opacity';
import {
  buildSpaceFloorShape,
  buildSpaceWallSegments,
  getSpacePolygonVerticesFeet,
  localPointFromVertexFeet,
  verticesToLocal3D,
  WallSegment3D
} from 'utils/3d-polygon';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import { local3DEdgeFromRun } from 'utils/local-3d-edge-from-run';
import { edgeItemOpeningTs } from 'utils/opening-drag';
import { getSpaceEdgeRunsFromFeet } from 'utils/opening-layout';
import { EdgeRunType } from 'utils/opening-position-types';
import { openingLayoutsFromEdgeItems } from 'utils/opening-run-layout';
import { OpeningRunLayoutType } from 'utils/opening-run-layout-types';
import { spaceOpeningRunLayouts } from 'utils/opening-run-wall';
import {
  getHiddenIntervalsPerEdge,
  getOpenToNeighborPolygonsAbsolute,
  getVisibleIntervalsPerEdge,
  spaceToAbsolutePolygon
} from 'utils/polygon-shared-edges';
import {
  boundingBoxOfVertices,
  isPolygonGridSpace,
  polygonInteriorPoint
} from 'utils/space-polygon';
import {
  findSpaceById,
  getEdgeItemsFromSpace,
  shouldShowSpaceLabel
} from 'utils/structure';
import doorMetalTexture from './assets/door-metal.jpg';
import doorWoodTexture from './assets/door-wood.jpg';
import floorTexture from './assets/floor.jpg';
import garageDoorTexture from './assets/garage-door.jpg';
import glassDoorTexture from './assets/glass-door.jpg';
import petDoorTexture from './assets/pet-door.jpg';
import wallTexture from './assets/wall.jpg';
import windowTexture from './assets/window.jpg';

type Space3DData = {
  id: string;
  spaceId: string;
  label: string;
  position: PositionAndDimensionsType;
  vertices?: PositionType[];
  type: 'new-space' | 'space' | 'overlay';
  itemId?: number;
  sectionId?: string;
  spacesInSection?: number;
  sectionColor?: { bg: string; border: string; text: string };
};

type Space3DProps = {
  space: Space3DData;
  allSpaces?: Space3DData[];
  yOffset?: number;
  opacity?: number;
  showLabel?: boolean;
  levelType?: string;
};

type PolygonWallMeshesProps = {
  segments: WallSegment3D[];
  wallHeight: number;
  wallThickness: number;
  wallTexture: Texture;
  opacity: number;
};

function PolygonWallMeshes({
  segments,
  wallHeight,
  wallThickness,
  wallTexture: wall,
  opacity
}: PolygonWallMeshesProps) {
  return (
    <>
      {segments.map((segment, index) => {
        const wallTextureClone = wall.clone();
        wallTextureClone.repeat.set(segment.length * 0.5, wallHeight * 0.5);

        return (
          <mesh
            key={`wall-${index}`}
            position={[segment.centerX, wallHeight / 2, segment.centerZ]}
            rotation={[0, segment.angle, 0]}
            renderOrder={getOpacityRenderOrder(opacity)}
          >
            <boxGeometry args={[segment.length, wallHeight, wallThickness]} />
            <meshStandardMaterial
              map={wallTextureClone}
              {...getOpacityMaterialProps(opacity)}
            />
          </mesh>
        );
      })}
    </>
  );
}

type EdgeItemMeshProps = {
  items: EdgeItemType[];
  run: EdgeRunType;
  bboxWidthFeet: number;
  bboxHeightFeet: number;
  scale: number;
  wallHeight: number;
  opacity: number;
  doorWoodTex: Texture;
  doorMetalTex: Texture;
  garageDoorTex: Texture;
  petDoorTex: Texture;
  windowTex: Texture;
  glassDoorTex: Texture;
};

function EdgeItemMeshes({
  items,
  run,
  bboxWidthFeet,
  bboxHeightFeet,
  scale,
  wallHeight,
  opacity,
  doorWoodTex,
  doorMetalTex,
  garageDoorTex,
  petDoorTex,
  windowTex,
  glassDoorTex
}: EdgeItemMeshProps) {
  if (items.length === 0) return null;

  const localEdge = local3DEdgeFromRun(
    run,
    bboxWidthFeet,
    bboxHeightFeet,
    scale
  );
  const dx = localEdge.p2.x - localEdge.p1.x;
  const dz = localEdge.p2.z - localEdge.p1.z;
  const edgeLength = Math.hypot(dx, dz);
  if (edgeLength < 1e-6) return null;
  const angle = -Math.atan2(dz, dx);
  const itemTs = edgeItemOpeningTs(items, run.lengthFt);

  const displays = items.map((item) =>
    computeEdgeItemDisplay(item, item.width, {
      doorWoodTex,
      doorMetalTex,
      garageDoorTex,
      petDoorTex
    })
  );

  return (
    <>
      {items.map((item, index) => {
        const { itemSize, itemHeight, standsOnFloor, usesGlassMaterial } =
          displays[index];
        const t = itemTs[index];
        const cx = localEdge.p1.x + t * dx;
        const cz = localEdge.p1.z + t * dz;
        const yPosition = standsOnFloor ? itemHeight / 2 : wallHeight / 2;
        const itemOpacity = usesGlassMaterial ? opacity * 0.7 : opacity;

        return (
          <mesh
            key={item.id}
            position={[cx, yPosition, cz]}
            rotation={[0, angle, 0]}
            renderOrder={getOpacityRenderOrder(itemOpacity)}
          >
            <boxGeometry args={[itemSize, itemHeight, 0.2]} />
            <meshStandardMaterial
              map={edgeItemMaterialMap(
                displays[index],
                windowTex,
                glassDoorTex
              )}
              {...getOpacityMaterialProps(itemOpacity)}
            />
          </mesh>
        );
      })}
    </>
  );
}

export default function Space3D({
  space,
  allSpaces = [],
  yOffset = 0,
  opacity = 1,
  showLabel = true,
  levelType = 'level'
}: Space3DProps) {
  const structure = useAppSelector((state) => state.structure.structure);
  const { showZones } = useAppSelector((state) => state.ui);
  const { projectSettings } = useAppSelector((state) => state.project);

  const homeOrientation = useMemo(() => {
    const orientationSetting = projectSettings.find(
      (s) => s.code === 'structure_orientation'
    );
    return orientationSetting?.value as ExposureDirectionType | undefined;
  }, [projectSettings]);
  const [
    floor,
    wall,
    doorWoodTex,
    doorMetalTex,
    garageDoorTex,
    petDoorTex,
    windowTex,
    glassDoorTex
  ] = useTexture([
    floorTexture,
    wallTexture,
    doorWoodTexture,
    doorMetalTexture,
    garageDoorTexture,
    petDoorTexture,
    windowTexture,
    glassDoorTexture
  ]);
  const scale = 0.5;
  const wallHeight = levelType === 'crawlspace' ? 1 : 2.5;
  const wallThickness = 0.1;
  const tileSizeInFeet = 10;

  const floorClone = floor.clone();
  floorClone.wrapS = RepeatWrapping;
  floorClone.wrapT = RepeatWrapping;

  wall.wrapS = RepeatWrapping;
  wall.wrapT = RepeatWrapping;

  const computeShouldShowLabel = () => {
    return shouldShowSpaceLabel(space, allSpaces, structure, showLabel);
  };

  const edgeItems = useMemo((): EdgeItemsType => {
    return getEdgeItemsFromSpace(structure, space.spaceId, homeOrientation);
  }, [structure, space.spaceId, homeOrientation]);

  const polygonVerticesFeet = useMemo(
    () =>
      getSpacePolygonVerticesFeet(
        space.vertices,
        space.position.width,
        space.position.height
      ),
    [space.vertices, space.position.width, space.position.height]
  );

  const polygonBboxFeet = useMemo(
    () => boundingBoxOfVertices(polygonVerticesFeet),
    [polygonVerticesFeet]
  );

  const x = (space.position.x + polygonBboxFeet.width / 2) * scale;
  const z = (space.position.y + polygonBboxFeet.height / 2) * scale;

  const localVertices = useMemo(
    () =>
      verticesToLocal3D(
        polygonVerticesFeet,
        polygonBboxFeet.width,
        polygonBboxFeet.height,
        scale
      ),
    [polygonVerticesFeet, polygonBboxFeet, scale]
  );

  const floorShape = useMemo(
    () => buildSpaceFloorShape(localVertices),
    [localVertices]
  );

  const floorGeometry = useMemo(() => {
    const geo = new ShapeGeometry(floorShape);
    const positionAttr = geo.getAttribute('position');
    const uvs = new Float32Array(positionAttr.count * 2);
    for (let i = 0; i < positionAttr.count; i += 1) {
      const sx = positionAttr.getX(i);
      const sy = positionAttr.getY(i);
      uvs[i * 2] = sx / scale / tileSizeInFeet;
      uvs[i * 2 + 1] = -sy / scale / tileSizeInFeet;
    }
    geo.setAttribute('uv', new BufferAttribute(uvs, 2));
    return geo;
  }, [floorShape, scale, tileSizeInFeet]);

  const ceilingGeometry = useMemo(
    () => new ShapeGeometry(floorShape),
    [floorShape]
  );

  const visibleIntervalsPerEdge = useMemo(() => {
    const currentSpaceType = findSpaceById(structure, space.spaceId);
    const neighborPolygons = currentSpaceType
      ? getOpenToNeighborPolygonsAbsolute(currentSpaceType.openTo, allSpaces, 1)
      : [];
    if (neighborPolygons.length === 0) {
      return polygonVerticesFeet.map(() => [{ start: 0, end: 1 }]);
    }
    const spaceAbs = spaceToAbsolutePolygon(space, 1);
    const hidden = getHiddenIntervalsPerEdge(spaceAbs, neighborPolygons);
    return getVisibleIntervalsPerEdge(hidden);
  }, [structure, space, allSpaces, polygonVerticesFeet]);

  const wallSegments = useMemo(
    () =>
      buildSpaceWallSegments(
        localVertices,
        wallThickness,
        visibleIntervalsPerEdge
      ),
    [localVertices, wallThickness, visibleIntervalsPerEdge]
  );

  const labelLocalPoint = useMemo(() => {
    const interior = polygonInteriorPoint(polygonVerticesFeet);
    if (!interior) return { x: 0, z: 0 };
    return localPointFromVertexFeet(
      interior,
      polygonBboxFeet.width,
      polygonBboxFeet.height,
      scale
    );
  }, [polygonVerticesFeet, polygonBboxFeet, scale]);

  const isAttic = levelType === 'attic';

  const owningLevel = useMemo(
    () =>
      structure.find((level) =>
        level.sections.some((section) =>
          section.spaces.some((s) => s.uniqueId === space.spaceId)
        )
      ),
    [structure, space.spaceId]
  );

  const spaceLevelFootprint = useMemo(
    () => getEffectiveFootprintPolygons(owningLevel),
    [owningLevel]
  );

  const edgeRuns = useMemo(
    () =>
      getSpaceEdgeRunsFromFeet(
        {
          position: {
            x: space.position.x,
            y: space.position.y,
            width: polygonBboxFeet.width,
            height: polygonBboxFeet.height
          },
          vertices: polygonVerticesFeet
        },
        spaceLevelFootprint
      ),
    [
      space.position.x,
      space.position.y,
      polygonBboxFeet,
      polygonVerticesFeet,
      spaceLevelFootprint
    ]
  );

  const openingLayouts = useMemo((): OpeningRunLayoutType[] => {
    const isPolygonSpace = isPolygonGridSpace(
      space.vertices,
      polygonBboxFeet.width,
      polygonBboxFeet.height
    );
    const spaceType = isPolygonSpace
      ? findSpaceById(structure, space.spaceId)
      : undefined;
    const polygonLayouts = spaceType
      ? spaceOpeningRunLayouts(spaceType, owningLevel, homeOrientation)
      : [];
    return polygonLayouts.length > 0
      ? polygonLayouts
      : openingLayoutsFromEdgeItems(edgeItems, edgeRuns);
  }, [
    structure,
    owningLevel,
    space.spaceId,
    space.vertices,
    polygonBboxFeet,
    homeOrientation,
    edgeItems,
    edgeRuns
  ]);

  const hslToColor = (hsl: string): Color => {
    const match = hsl.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (!match) {
      return new Color(0.5, 0.5, 0.5);
    }

    const h = parseInt(match[1], 10) / 360;
    const s = parseInt(match[2], 10) / 100;
    const l = parseInt(match[3], 10) / 100;

    const color = new Color();
    color.setHSL(h, s, l);
    return color;
  };

  const darkenHsl = (hsl: string, darkenAmount: number = 0.4): string => {
    const match = hsl.match(/hsl\((\d+),\s*(\d+)%,\s*(\d+)%\)/);
    if (!match) {
      return hsl;
    }

    const h = parseInt(match[1], 10);
    const s = parseInt(match[2], 10);
    const l = Math.max(10, parseInt(match[3], 10) - darkenAmount * 100);

    return `hsl(${h}, ${s}%, ${l}%)`;
  };

  const getLabelColor = (): string => {
    if (showZones && space.sectionColor) {
      return darkenHsl(space.sectionColor.bg);
    }
    return 'white';
  };

  const labelX = labelLocalPoint.x;
  const labelZ = labelLocalPoint.z;
  const labelY = wallHeight + 0.2;

  return (
    <group position={[x, yOffset, z]}>
      <mesh
        position={[0, 0.01, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
        renderOrder={getOpacityRenderOrder(opacity)}
      >
        <primitive object={floorGeometry} attach='geometry' />
        <meshStandardMaterial
          map={floorClone}
          side={DoubleSide}
          {...getOpacityMaterialProps(opacity)}
        />
      </mesh>
      {!isAttic && (
        <>
          {space.sectionColor && (
            <mesh
              position={[0, wallHeight, 0]}
              rotation={[-Math.PI / 2, 0, 0]}
              renderOrder={OVERLAY_RENDER_ORDER}
            >
              <primitive object={ceilingGeometry} attach='geometry' />
              <meshStandardMaterial
                color={hslToColor(space.sectionColor.bg)}
                side={DoubleSide}
                {...getOpacityMaterialProps(Math.max(0.7, opacity * 0.9))}
              />
            </mesh>
          )}
          <PolygonWallMeshes
            segments={wallSegments}
            wallHeight={wallHeight}
            wallThickness={wallThickness}
            wallTexture={wall}
            opacity={opacity}
          />
          {openingLayouts.map((layout, index) => (
            <EdgeItemMeshes
              key={`edge-items-${layout.run.edgeIndex ?? index}-${
                layout.run.edge
              }`}
              items={layout.items}
              run={layout.run}
              bboxWidthFeet={polygonBboxFeet.width}
              bboxHeightFeet={polygonBboxFeet.height}
              scale={scale}
              wallHeight={wallHeight}
              opacity={opacity}
              doorWoodTex={doorWoodTex}
              doorMetalTex={doorMetalTex}
              garageDoorTex={garageDoorTex}
              petDoorTex={petDoorTex}
              windowTex={windowTex}
              glassDoorTex={glassDoorTex}
            />
          ))}
        </>
      )}
      {computeShouldShowLabel() && (
        <Text
          position={[labelX, labelY, labelZ]}
          fontSize={0.7}
          color={getLabelColor()}
          anchorX='center'
          anchorY='middle'
          rotation={[-Math.PI / 2, 0, 0]}
        >
          {space.label}
        </Text>
      )}
    </group>
  );
}
